import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import {
  fetchTimetable,
  type TimetableSession,
  type TimetableFiltersParams,
} from "~/widgets/lib/api";
import { cn } from "~/widgets/lib/utils";
import { pushToGoogleAds } from "~/widgets/lib/tracking";
import { SessionCard, DateHeader } from "./SessionCard";
import { WaitingListPortal } from "../WaitingListPortal";
import { TimetableFilters } from "./TimetableFilters";

const buttonStyleClasses = {
  rounded: "rounded-lg",
  square: "rounded-none",
  pill: "rounded-full",
} as const;

// Helper to group sessions by date
function groupSessionsByDate(sessions: TimetableSession[], timezone: string) {
  const groups: { date: string; dateKey: string; sessions: TimetableSession[] }[] = [];

  for (const session of sessions) {
    const sessionDate = new Date(session.startDateTime);
    const dateKey = sessionDate.toLocaleDateString("en-GB", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    let group = groups.find(g => g.dateKey === dateKey);
    if (!group) {
      group = { date: session.startDateTime, dateKey, sessions: [] };
      groups.push(group);
    }
    group.sessions.push(session);
  }

  return groups;
}

type AvailabilityFilter = "all" | "sold-out" | "available" | "few-spots";

export type TimetableProps = {
  /** School slug (e.g., "samadhi-yoga-and-well-being") - same as used in widget URL */
  businessSlug: string;
  apiUrl?: string;
  primaryColor?: string;
  theme?: "light" | "dark";
  /** Google Ads conversion ID (e.g., "AW-123456789") for automatic conversion tracking */
  gtagConversionId?: string;
  /** Google Ads conversion label for automatic conversion tracking */
  gtagConversionLabel?: string;

  /** Display mode: "inline" renders the timetable in place (default), "popup" renders a button that opens the timetable in a full-screen overlay */
  mode?: "popup" | "inline";
  /** Button text shown in popup mode (default "View Timetable") */
  buttonText?: string;
  /** Custom button class name appended in popup mode */
  buttonClassName?: string;
  /** Button style variant in popup mode (default "pill") */
  buttonStyle?: "rounded" | "square" | "pill";

  /** Inline only: show only these classes (comma-separated ids)… */
  courseIds?: string;
  /** …and the classes with these tags (comma-separated ids). The API reads
   * the selection live (a Website v2 page made from a v1 landing page). */
  tagIds?: string;
};

// Loading skeleton for sessions
function SessionSkeleton() {
  return (
    <div className="flex flex-col sm:flex-row gap-3 p-4 bg-white rounded-lg border border-gray-200 animate-pulse">
      <div className="flex sm:flex-col items-center sm:items-center gap-2 sm:gap-0 sm:w-16 shrink-0">
        <div className="w-10 h-10 bg-gray-200 rounded" />
        <div className="w-8 h-4 bg-gray-200 rounded" />
      </div>
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
        <div className="h-3 bg-gray-200 rounded w-1/3" />
      </div>
      <div className="w-24 h-12 bg-gray-200 rounded-lg shrink-0" />
    </div>
  );
}

// Error display
function ErrorDisplay({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="w-12 h-12 mb-4 text-red-500">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="m15 9-6 6" />
          <path d="m9 9 6 6" />
        </svg>
      </div>
      <p className="text-gray-600 mb-4">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800"
      >
        Try Again
      </button>
    </div>
  );
}

// Empty state
function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="w-16 h-16 mb-4 text-gray-300">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        No sessions found
      </h3>
      <p className="text-gray-500 text-sm max-w-xs">
        {hasFilters
          ? "Try adjusting your filters to see more results."
          : "There are no upcoming sessions available at the moment."}
      </p>
    </div>
  );
}

type DateRange = {
  from: Date;
  to: Date;
};

// Helper to get default date range (today to 1 year from now)
function getDefaultDateRange(): DateRange {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextYear = new Date(today);
  nextYear.setFullYear(nextYear.getFullYear() + 1);
  return { from: today, to: nextYear };
}

const LONDON_LADY_SCHOOL_IDS = ["bmDxPjPqe7QoNLk76", "3t7gZeu8pz7AiAeAM"];
// In London Lady mode the initial unfiltered view shows this many upcoming sessions.
const LONDON_LADY_INITIAL_SESSION_LIMIT = 30;

function TimetableInner({
  businessSlug,
  apiUrl,
  primaryColor = "#03c2b9",
  theme: _theme = "light",
  gtagConversionId,
  gtagConversionLabel,
  courseIds,
  tagIds,
}: Omit<TimetableProps, "mode" | "buttonText" | "buttonClassName" | "buttonStyle">) {
  // Filter state
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [selectedTutorIds, setSelectedTutorIds] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] =
    useState<AvailabilityFilter>("all");
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);

  // London Lady detection (set after first fetch — see effect below).
  const [isLondonLady, setIsLondonLady] = useState(false);
  // London Lady week filter is opt-in: the initial view shows the next N sessions
  // unfiltered, and only switches to a single-week view once the user picks one.
  const [londonLadyWeekActive, setLondonLadyWeekActive] = useState(false);

  // Loading state for booking
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);

  // Track when checkout overlay is open - hide timetable to prevent double scrollbar
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const [waitingListSession, setWaitingListSession] =
    useState<TimetableSession | null>(null);

  // Ref to track the current checkout overlay
  const checkoutOverlayRef = useRef<HTMLElement | null>(null);

  // Scroll container ref for infinite scroll
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Build filter params
  const filterParams: TimetableFiltersParams = useMemo(() => {
    // London Lady's initial view ignores the week filter and shows all upcoming sessions
    // capped at LONDON_LADY_INITIAL_SESSION_LIMIT until the user picks a specific week.
    const useDefaultRange = isLondonLady && !londonLadyWeekActive;
    const range = useDefaultRange ? getDefaultDateRange() : dateRange;
    return {
      coursesIds: selectedCourseIds,
      locationIds: selectedLocationIds,
      tutorIds: selectedTutorIds,
      availability: selectedAvailability,
      from: range.from.toISOString().split("T")[0],
      to: range.to.toISOString().split("T")[0],
      limit: isLondonLady ? LONDON_LADY_INITIAL_SESSION_LIMIT : 20,
      onlyCourseIds: courseIds || undefined,
      onlyTagIds: tagIds || undefined,
    };
  }, [
    courseIds,
    tagIds,
    isLondonLady,
    londonLadyWeekActive,
    selectedCourseIds,
    selectedLocationIds,
    selectedTutorIds,
    selectedAvailability,
    dateRange,
  ]);

  // Fetch timetable data with infinite scroll
  // Uses the same endpoint as the widget: /widget/pages/timetable/:slug
  const {
    data,
    isLoading: isLoadingTimetable,
    error: timetableError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchTimetable,
  } = useInfiniteQuery({
    queryKey: ["timetable", businessSlug, filterParams],
    queryFn: async ({ pageParam = 1 }) => {
      return fetchTimetable(
        businessSlug,
        { ...filterParams, page: pageParam },
        apiUrl,
      );
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (sum, page) => sum + (page.sessions?.length || 0),
        0,
      );
      if (totalLoaded < (lastPage.total || 0)) {
        return allPages.length + 1;
      }
      return undefined;
    },
    enabled: !!businessSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
  });

  // Flatten paginated data
  const flattenedData = useMemo(() => {
    if (!data?.pages?.length) return null;

    const firstPage = data.pages[0];
    const allSessions = data.pages.flatMap((page) => page.sessions || []);

    return {
      ...firstPage,
      sessions: allSessions,
    };
  }, [data]);

  const {
    courses = [],
    locations = [],
    tutors = [],
    sessions = [],
  } = flattenedData ?? {};

  // Handle infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when 200px from bottom
    if (scrollHeight - scrollTop - clientHeight < 200) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Handle book now click - create checkout overlay web component
  const handleBookNow = useCallback(
    (session: TimetableSession) => {
      // Get the school ID from the session's course or first course
      const schoolId = session.course?.schoolId || courses[0]?.schoolId || "";

      // Create the checkout overlay web component
      const overlay = document.createElement("baluu-checkout-overlay");
      overlay.setAttribute("listing-id", session.courseID);
      overlay.setAttribute("business-id", schoolId);
      if (apiUrl) overlay.setAttribute("api-url", apiUrl);
      if (primaryColor) overlay.setAttribute("primary-color", primaryColor);

      // For term sessions, pass the eventId (term ID) so checkout selects the first session of the term
      // For regular sessions, pass the session ID
      const sessionIdToPass = session.isTerm && (session as any).eventId
        ? (session as any).eventId
        : session._id;
      overlay.setAttribute("initial-session-id", sessionIdToPass);

      // Store reference
      checkoutOverlayRef.current = overlay;

      // Hide timetable while checkout is open to prevent double scrollbar
      setIsCheckoutOpen(true);

      // Append to document body for proper full-screen overlay
      document.body.appendChild(overlay);

      // Listen for close event
      const handleClose = () => {
        setLoadingSessionId(null);
        setIsCheckoutOpen(false); // Show timetable again
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
        checkoutOverlayRef.current = null;
        // Remove listeners from document
        document.removeEventListener("baluu:checkout-close", handleClose);
        document.removeEventListener("baluu:booking-completed", handleComplete);
      };

      // Listen for booking completed event
      const handleComplete = (event: Event) => {
        const customEvent = event as CustomEvent;
        const detail = customEvent.detail || {};

        const completionEvent = new CustomEvent(
          "baluu:timetable-booking-completed",
          {
            bubbles: true,
            composed: true,
            detail,
          },
        );
        document.dispatchEvent(completionEvent);

        if (gtagConversionId && gtagConversionLabel) {
          pushToGoogleAds(gtagConversionId, gtagConversionLabel, {
            listingId: detail.listingId,
            businessId: detail.businessId,
            purchaseId: detail.purchaseId,
            total: detail.total,
            currency: detail.currency,
          });
        }

        setTimeout(() => {
          handleClose();
        }, 3000);
      };

      // Listen on document since CheckoutOverlay dispatches events there
      document.addEventListener("baluu:checkout-close", handleClose);
      document.addEventListener("baluu:booking-completed", handleComplete);
    },
    [courses, apiUrl, primaryColor, gtagConversionId, gtagConversionLabel],
  );

  const handleJoinWaitingList = useCallback((session: TimetableSession) => {
    setWaitingListSession(session);
  }, []);

  const handleWaitingListClose = useCallback(() => {
    setWaitingListSession(null);
  }, []);

  // Cleanup checkout overlay on unmount
  useEffect(() => {
    return () => {
      if (checkoutOverlayRef.current?.parentNode) {
        checkoutOverlayRef.current.parentNode.removeChild(
          checkoutOverlayRef.current,
        );
      }
    };
  }, []);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    setSelectedCourseIds([]);
    setSelectedLocationIds([]);
    setSelectedTutorIds([]);
    setSelectedAvailability("all");
    setDateRange(getDefaultDateRange());
    setLondonLadyWeekActive(false);
  }, []);

  // Handle date range change
  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
  }, []);

  // Helper to get location for a session
  const getLocation = useCallback(
    (locationId?: string) => {
      if (!locationId) return undefined;
      return locations.find((l) => l._id === locationId);
    },
    [locations],
  );

  // Helper to get tutor for a session
  const getTutor = useCallback(
    (tutorId?: string) => {
      if (!tutorId) return undefined;
      return tutors.find((t) => t._id === tutorId);
    },
    [tutors],
  );

  // Detect London Lady from the loaded data (runs after the first fetch — the
  // initial fetch will use the default limit/range until this flips on).
  useEffect(() => {
    const firstPage = data?.pages?.[0] as any;
    const schoolId = firstPage?.school?._id || courses[0]?.schoolId || "";
    const next = !!schoolId && LONDON_LADY_SCHOOL_IDS.includes(schoolId);
    setIsLondonLady((prev) => (prev === next ? prev : next));
  }, [data?.pages?.[0], courses]);

  const hasFilters =
    selectedCourseIds.length > 0 ||
    selectedLocationIds.length > 0 ||
    selectedTutorIds.length > 0 ||
    selectedAvailability !== "all";

  const isLoading = isLoadingTimetable;
  const error = timetableError;
  // Get timezone from the first course, or fall back to embedded session course timezone
  const timezone = courses[0]?.timezone || sessions[0]?.course?.timezone || "Europe/London";

  // Group sessions by date for display
  const groupedSessions = useMemo(() => {
    return groupSessionsByDate(sessions, timezone);
  }, [sessions, timezone]);

  // Hide timetable when checkout overlay is open to prevent double scrollbar
  if (isCheckoutOpen) {
    return null;
  }

  return (
    <div
      className="timetable-widget h-full flex flex-col bg-gray-50"
      style={{
        // minHeight inline (not min-h-[400px]): embedded host pages can mangle the
        // injected CSS and drop escaped selectors, so core layout avoids
        // arbitrary-value classes.
        minHeight: 400,
        fontFamily:
          'var(--font-sans, "Founders Grotesk", -apple-system, BlinkMacSystemFont, sans-serif)',
      }}
    >
      {/* Sticky filters */}
      <div className="sticky top-0 z-10 shrink-0">
        <TimetableFilters
          courses={courses}
          locations={locations}
          tutors={tutors}
          selectedCourseIds={selectedCourseIds}
          selectedLocationIds={selectedLocationIds}
          selectedTutorIds={selectedTutorIds}
          selectedAvailability={selectedAvailability}
          dateRange={dateRange}
          onCourseChange={setSelectedCourseIds}
          onLocationChange={setSelectedLocationIds}
          onTutorChange={setSelectedTutorIds}
          onAvailabilityChange={setSelectedAvailability}
          onDateRangeChange={handleDateRangeChange}
          onReset={handleResetFilters}
          primaryColor={primaryColor}
          businessSlug={businessSlug}
          apiUrl={apiUrl}
          showWeekFilter={isLondonLady}
          weekFilterActive={londonLadyWeekActive}
          onWeekFilterActiveChange={setLondonLadyWeekActive}
        />
      </div>

      {/* Scrollable content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4"
      >
        {/* Loading state */}
        {isLoading && sessions.length === 0 && (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <SessionSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Error state */}
        {error && sessions.length === 0 && (
          <ErrorDisplay
            message={
              error instanceof Error ? error.message : "Failed to load sessions"
            }
            onRetry={() => refetchTimetable()}
          />
        )}

        {/* Empty state */}
        {!isLoading && !error && sessions.length === 0 && (
          <EmptyState hasFilters={hasFilters} />
        )}

        {/* Sessions list grouped by date */}
        {sessions.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {groupedSessions.map((group) => (
              <div key={group.dateKey}>
                {/* Date header */}
                <DateHeader dateString={group.date} timezone={timezone} londonLadyMode={isLondonLady} />

                {/* Sessions for this date */}
                {group.sessions.map((session) => (
                  <SessionCard
                    key={session._id}
                    session={session}
                    location={getLocation(session.locationId)}
                    tutor={getTutor(session.tutor)}
                    timezone={timezone}
                    onBookNow={handleBookNow}
                    onJoinWaitingList={handleJoinWaitingList}
                    isLoading={loadingSessionId === session._id}
                    londonLadyMode={isLondonLady}
                  />
                ))}
              </div>
            ))}

            {/* Load more button */}
            {hasNextPage && (
              <div className="p-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className={cn(
                    "w-full py-3 px-4 text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm",
                    isFetchingNextPage
                      ? "bg-muted text-muted-foreground cursor-not-allowed"
                      : "text-primary-foreground bg-primary hover:bg-primary/90 active:bg-primary/80"
                  )}
                >
                  {isFetchingNextPage ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                      Load more sessions
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {waitingListSession && (
        <WaitingListPortal
          isOpen={Boolean(waitingListSession)}
          listingId={waitingListSession.courseID}
          sessionId={waitingListSession._id}
          eventId={
            waitingListSession.isTerm
              ? (waitingListSession as any).eventId
              : undefined
          }
          apiUrl={apiUrl}
          primaryColor={primaryColor}
          businessSlug={businessSlug}
          onClose={handleWaitingListClose}
        />
      )}
    </div>
  );
}

export function Timetable({
  mode = "inline",
  buttonText = "View Timetable",
  buttonClassName,
  buttonStyle = "pill",
  businessSlug,
  apiUrl,
  primaryColor = "#03c2b9",
  theme = "light",
  gtagConversionId,
  gtagConversionLabel,
  courseIds,
  tagIds,
}: TimetableProps) {
  const overlayRef = useRef<HTMLElement | null>(null);

  const handleClose = useCallback(() => {
    if (overlayRef.current?.parentNode) {
      overlayRef.current.parentNode.removeChild(overlayRef.current);
      overlayRef.current = null;
    }
  }, []);

  const handleOpen = useCallback(() => {
    const overlay = document.createElement("baluu-timetable-overlay");

    overlay.setAttribute("business-slug", businessSlug);
    if (apiUrl) overlay.setAttribute("api-url", apiUrl);
    if (primaryColor) overlay.setAttribute("primary-color", primaryColor);
    if (theme) overlay.setAttribute("theme", theme);
    if (gtagConversionId)
      overlay.setAttribute("gtag-conversion-id", gtagConversionId);
    if (gtagConversionLabel)
      overlay.setAttribute("gtag-conversion-label", gtagConversionLabel);

    overlayRef.current = overlay;
    document.body.appendChild(overlay);

    const handleOverlayClose = () => {
      handleClose();
      document.removeEventListener(
        "baluu:timetable-close",
        handleOverlayClose,
      );
    };
    document.addEventListener("baluu:timetable-close", handleOverlayClose);
  }, [
    businessSlug,
    apiUrl,
    primaryColor,
    theme,
    gtagConversionId,
    gtagConversionLabel,
    handleClose,
  ]);

  useEffect(() => {
    return () => {
      if (overlayRef.current?.parentNode) {
        overlayRef.current.parentNode.removeChild(overlayRef.current);
      }
    };
  }, []);

  if (mode === "inline") {
    return (
      <TimetableInner
        businessSlug={businessSlug}
        apiUrl={apiUrl}
        primaryColor={primaryColor}
        theme={theme}
        gtagConversionId={gtagConversionId}
        gtagConversionLabel={gtagConversionLabel}
        courseIds={courseIds}
        tagIds={tagIds}
      />
    );
  }

  const borderRadiusClass =
    buttonStyleClasses[buttonStyle] || buttonStyleClasses.pill;

  return (
    <div className="baluu-widget-root">
      <button
        type="button"
        onClick={handleOpen}
        className={cn(
          // Stable hook class for dashboard "Custom CSS" overrides - do not rename
          "baluu-timetable-popup-button",
          "inline-flex items-center justify-center",
          "px-6 py-3 font-medium text-base",
          "bg-primary text-primary-foreground",
          "transition-all cursor-pointer",
          "hover:opacity-90 hover:shadow-lg",
          "active:scale-95",
          borderRadiusClass,
          buttonClassName,
        )}
      >
        {buttonText}
      </button>
    </div>
  );
}
